import React, { useState, useEffect } from 'react'
import InfoCard from '../../components/shared/InfoCard'
import Modal from '../../components/shared/Modal'
import { MarketEMPsService } from '../../services/market-emps.service'
import { MarketEMP } from '../../types/database'

interface EMPSectionProps {
  modelId: string
}

export default function EMPSection({ modelId }: EMPSectionProps) {
  const [emps, setEmps] = useState<MarketEMP[]>([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingEMP, setEditingEMP] = useState<MarketEMP | null>(null)
  const [empForm, setEmpForm] = useState({ name: '', description: '', cost_per_hour: '' })

  useEffect(() => {
    fetchEMPs()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId])

  const fetchEMPs = async () => {
    try {
      const modelIdNum = parseInt(modelId, 10)
      const empsData = await MarketEMPsService.getAllByModelId(modelIdNum)
      setEmps(empsData)
    } catch (err: any) {
      // Error fetching EMPs
    }
  }

  const handleAddEMP = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!empForm.name.trim()) return

    try {
      const empData = {
        model_id: parseInt(modelId, 10),
        name: empForm.name.trim(),
        description: empForm.description.trim() || null,
        cost_per_hour: empForm.cost_per_hour ? parseFloat(empForm.cost_per_hour) : null
      }

      await MarketEMPsService.create(empData)
      setEmpForm({ name: '', description: '', cost_per_hour: '' })
      setShowAddModal(false)
      await fetchEMPs()
    } catch (err: any) {
      if (err.code === '23505' && err.message.includes('market_emps_model_id_name_key')) {
        alert(`An EMP with the name "${empForm.name.trim()}" already exists for this model. Please choose a different name.`)
      } else {
        alert('Failed to add EMP: ' + err.message)
      }
    }
  }

  const handleEditEMP = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingEMP || !empForm.name.trim()) return

    try {
      await MarketEMPsService.update(editingEMP.id, {
        name: empForm.name.trim(),
        description: empForm.description.trim() || null,
        cost_per_hour: empForm.cost_per_hour ? parseFloat(empForm.cost_per_hour) : null
      })

      setEmpForm({ name: '', description: '', cost_per_hour: '' })
      setEditingEMP(null)
      setShowEditModal(false)
      await fetchEMPs()
    } catch (err: any) {
      if (err.code === '23505' && err.message.includes('market_emps_model_id_name_key')) {
        alert(`An EMP with the name "${empForm.name.trim()}" already exists for this model. Please choose a different name.`)
      } else {
        alert('Failed to update EMP: ' + err.message)
      }
    }
  }

  const handleDeleteEMP = async (empId: string) => {
    if (!window.confirm('Are you sure you want to delete this EMP?')) return

    try {
      await MarketEMPsService.delete(empId)
      await fetchEMPs()
    } catch (err: any) {
      alert('Failed to delete EMP: ' + err.message)
    }
  }

  const handleToggleEMPActive = async (empId: string, isActive: boolean) => {
    try {
      if (isActive) {
        const activeEMPs = emps.filter(emp => emp.is_active && emp.id !== empId)
        if (activeEMPs.length > 0) {
          const activeNames = activeEMPs.map(emp => emp.name).join(', ')
          if (!window.confirm(`Activating this EMP will deactivate the currently active EMP(s): ${activeNames}. Only one EMP can be active at a time. Continue?`)) {
            return
          }
        }
      }

      await MarketEMPsService.toggleActive(empId, isActive)
      await fetchEMPs()
    } catch (err: any) {
      alert('Failed to update EMP status: ' + err.message)
    }
  }

  const empFormFields = (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">EMP Name *</label>
        <input
          type="text"
          value={empForm.name}
          onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })}
          placeholder="e.g., MSP Gold, JSSI, Corporate Angel"
          className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
        <textarea
          value={empForm.description}
          onChange={(e) => setEmpForm({ ...empForm, description: e.target.value })}
          placeholder="Brief description of this maintenance program"
          rows={3}
          className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cost Per Hour (Optional)</label>
        <input
          type="number"
          step="0.01"
          value={empForm.cost_per_hour}
          onChange={(e) => setEmpForm({ ...empForm, cost_per_hour: e.target.value })}
          placeholder="e.g., 150.00"
          className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
        />
      </div>
    </>
  )

  return (
    <>
      <InfoCard
        title="Engine Maintenance Programs (EMPs)"
        action={
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-purple-200 text-purple-950 hover:bg-purple-300 px-3 py-1 rounded text-sm font-medium transition-colors duration-150"
          >
            Add EMP
          </button>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Configure Engine Maintenance Programs for this market. Only one EMP can be active at a time.
            The active EMP's cost per hour is used in relative value calculations for aircraft not on an EMP.
          </p>
          {emps.length > 0 ? (
            <div className="space-y-2">
              {emps.map((emp) => (
                <div key={emp.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${emp.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                        {emp.name}
                      </span>
                      {emp.is_active ? (
                        <span className="px-2 py-1 text-xs bg-green-200 text-green-800 rounded font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                          Inactive
                        </span>
                      )}
                      {emp.cost_per_hour && (
                        <span className="text-xs text-gray-500">
                          ${emp.cost_per_hour}/hr
                        </span>
                      )}
                    </div>
                    {emp.description && (
                      <p className="text-sm text-gray-600 mt-1">{emp.description}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleEMPActive(emp.id, !emp.is_active)}
                      className={`px-2 py-1 text-xs rounded ${
                        emp.is_active
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {emp.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingEMP(emp)
                        setEmpForm({
                          name: emp.name,
                          description: emp.description || '',
                          cost_per_hour: emp.cost_per_hour?.toString() || ''
                        })
                        setShowEditModal(true)
                      }}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteEMP(emp.id)}
                      className="px-2 py-1 text-xs bg-red-100 text-red-800 hover:bg-red-200 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No EMPs configured for this market. Add EMPs to standardize maintenance program options.
            </p>
          )}
        </div>
      </InfoCard>

      {/* Add EMP Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Engine Maintenance Program">
        <form onSubmit={handleAddEMP} className="space-y-4">
          {empFormFields}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false)
                setEmpForm({ name: '', description: '', cost_per_hour: '' })
              }}
              className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent bg-purple-950 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Add EMP
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit EMP Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Engine Maintenance Program">
        <form onSubmit={handleEditEMP} className="space-y-4">
          {empFormFields}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowEditModal(false)
                setEditingEMP(null)
                setEmpForm({ name: '', description: '', cost_per_hour: '' })
              }}
              className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent bg-purple-950 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Update EMP
            </button>
          </div>
        </form>
      </Modal>
    </>
  )
}

